import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { StateService } from '@uirouter/core';
import { Observable, Subscription } from 'rxjs';
import { map, take, takeWhile } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { LoginRequestStatus } from '../efaact-configuration/efaact-configuration.module';
import { LoggingService } from '../shared/services/logging/logging.service';
import * as rootActions from '../store/actions';
import * as fromCore from '../store/core';
import { IRememberMe } from './IRememberMe';
import { ISignInDialogData } from './ISignInDialogData';
import { SignInDialogComponent } from './sign-in/dialog/sign-in-dialog.component';
import * as fromAuth from './store';
import * as authActions from './store/auth.actions';

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService implements OnDestroy {
    private loggedInSub: Subscription;
    private authTokenSub: Subscription;
    private authSub: Subscription;
    private _authenticated = false;
    public bearerToken: string;
    private loggingIn = false;
    private active = true;
    constructor(
        private store: Store<fromAuth.State>,
        private dialog: MatDialog,
        private ss: StateService,
        @Inject(HttpClient) private http: HttpClient,
        @Inject(LoggingService) private log: LoggingService
    ) {
        this.loggedInSub = this.store
            .pipe(select(fromAuth.getLoggedIn), take(1))
            .subscribe(x => (this._authenticated = x));

        this.authTokenSub = this.store
            .pipe(
                select(fromAuth.getAuthToken),
                takeWhile(() => this.active)
            )
            .subscribe(x => {
                this.bearerToken = x;
            });

        this.store.dispatch(new fromCore.SetDateFormat('YYYY/MM/DD'));
    }

    ngOnDestroy(): void {
        if (this.loggedInSub) {
            this.loggedInSub.unsubscribe();
        }
        if (this.authTokenSub) {
            this.authTokenSub.unsubscribe();
        }
        if (this.authSub) {
            this.authSub.unsubscribe();
        }
        this.active = false;
    }
    public authenticate(
        authenticationRequest:
            | IAuthenticateRequest
            | IAuthenticateSecurityQuestion
            | IAuthenticateAccessCode
    ): Observable<IAuthenticateResponse> {
        const url = `${environment.apiLocation}/Authentication`;
        this.log.verbose({
            type: 'AuthenticationService.authenticate',
            url,
            authenticationRequest
        });
        return this.http
            .post<IAuthenticateResponse>(url, authenticationRequest, {
                observe: 'response'
            })
            .pipe(map(resp => resp?.body));
    }

    public isAuthenticated(): boolean {
        return !!this.bearerToken;
    }

    public openDialog(authStatus?: LoginRequestStatus): void {
        this.log.verbose({
            type: 'AuthenticationService:openDialog',
            loggingIn: this.loggingIn,
            authStatus
        });
        const dialogConfig = new MatDialogConfig();
        if (
            !this.loggingIn &&
            (authStatus === LoginRequestStatus.Undefined ||
                authStatus ===
                    LoginRequestStatus.SubmitUsernamePasswordAndPIN ||
                !authStatus)
        ) {
            this.store.dispatch(new authActions.Logout());
        }

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;
        if (!this.loggingIn) {
            const loginRef = this.dialog.open(
                SignInDialogComponent,
                dialogConfig
            );
            this.loggingIn = true;
            loginRef
                .afterClosed()
                .pipe(takeWhile(() => this.active))
                .subscribe((data: ISignInDialogData) => {
                    this.loggingIn = false;
                    if (!data || data.cancel) {
                        this.store.dispatch(new rootActions.LogoutAction());
                        this.openDialog();
                        return;
                    }
                    this.log.verbose({
                        type: 'SignInComponent:afterClosed',
                        signInData: data
                    });

                    switch (data && data.status) {
                        case LoginRequestStatus.SelectAccessCodeMethod: // select access code method
                            this.store.dispatch(
                                new authActions.SendAccessCodeMethod(data)
                            );
                            break;
                        case LoginRequestStatus.AccessCodeSent: // AnswerSecurityQuestion
                            this.store.dispatch(
                                new authActions.SendAccessCode(data)
                            );
                            break;
                        case LoginRequestStatus.AnswerSecurityQuestion: // AnswerSecurityQuestion
                            this.store.dispatch(
                                new authActions.SendSecurityAnswer(data)
                            );
                            break;
                        case LoginRequestStatus.LoginSuccessful:
                            this.ss.go('main');
                            break;
                        default:
                            this.store.dispatch(
                                new authActions.SendUsernamePasswordPin(data)
                            );
                            break;
                    }

                    this.authenticate({
                        password: data.password,
                        username: data.username,
                        pin: data.pin,
                        Resetpassword: data.Resetpassword,
                        accessCodeDeliveryType: data.accessCodeDeliveryType,
                        accessCode: data.accessCode,
                        securityAnswer: data.securityAnswer
                    })
                        .pipe(takeWhile(() => this.active))
                        .subscribe(
                            (auth: IAuthenticateResponse) => {
                                this.handleAuthResponse(auth, data);
                            },
                            error => {
                                error.HasError = true;
                                error.ErrorMessage = error.message;
                                this.handleAuthResponse(error, null);
                            }
                        );
                });
        }
    }
    public logout(): void {
        this.log.verbose({ type: 'AuthenticationService:logout' });
        delete this.bearerToken;
        this.store.dispatch(new rootActions.LogoutAction());
    }

    handleAuthResponse(
        resp: IAuthenticateResponse,
        req: IAuthenticateRequest & IRememberMe
    ) {
        this.log.debug({
            type: 'AuthenticationService:handleAuthResponse',
            req,
            resp
        });
        // if (req.Resetpassword) {
        //     this.ss.go('main');
        //     return;
        // }
        if (resp.HasError) {
            this.store.dispatch(new authActions.LoginFailure(resp));
        }

        switch (resp.StatusCode) {
            case LoginRequestStatus.SelectAccessCodeMethod: // select access code method
                this.store.dispatch(
                    new authActions.LoginSelectAccessCode(resp)
                );
                break;
            case LoginRequestStatus.AccessCodeSent: // select access code method
                this.store.dispatch(new authActions.LoginAccessCodeSent(resp));
                break;
            case LoginRequestStatus.AnswerSecurityQuestion: // AnswerSecurityQuestion
                this.store.dispatch(
                    new authActions.LoginAnswerSecurityQuestion(resp)
                );
                break;
            case LoginRequestStatus.LoginSuccessful: // successfull login
                this.store.dispatch(new rootActions.LoginAction({ req, resp }));
                // this.authStore.dispatch(new authActions.LoginSuccess(resp));
                return;
                break;
            // default:
            //     this.openDialog(resp.StatusCode);
        }
        this.openDialog(resp.StatusCode);
    }
}

export interface IAccessCodeRequestMethod {
    Value: string;
    DisplayName: string;
}

export interface IAuthenticateResponse {
    StatusCode: LoginRequestStatus;
    StatusMessage: string;
    RemainingAttempts: number;
    MinutesUntilNextAttemptAllowed: number;
    AccessCodeRequestMethods: IAccessCodeRequestMethod[];
    SecurityQuestion: string;
    BearerToken: string;
    HasError: boolean;
    ErrorMessage: string;
    CustomerID?: string;
}

export interface IAuthenticateRequest {
    username?: string;
    password?: string;
    pin?: string;
    Resetpassword?: boolean;
    // AccessCodeDeliveryType: string;
    // AccessCode: string;
    // SecurityAnswer: string;
}

export interface IAuthenticateAccessCode extends IAuthenticateRequest {
    accessCodeDeliveryType: string;
    accessCode: string;
}

export interface IAuthenticateSecurityQuestion extends IAuthenticateAccessCode {
    securityAnswer: string;
}

export class AuthenticateResponse implements IAuthenticateResponse {
    StatusCode: number;
    StatusMessage: string;
    RemainingAttempts: number;
    MinutesUntilNextAttemptAllowed: number;
    AccessCodeRequestMethods: IAccessCodeRequestMethod[];
    SecurityQuestion: string;
    BearerToken: string;
    HasError: boolean;
    ErrorMessage: string;
}
